<template>
  <div class="custom-multi-select" v-closable="{ exclude: [], handler: 'onFocusOut' }">
    <b-input-group class="searchBox" size="sm">
      <b-input-group-text>
        <i class="fas fa-search"></i>
      </b-input-group-text>
      <b-input
        v-model="textValue"
        :placeholder="$t('common.searchBarPlaceholder')"
        autocomplete="off"
        class="custom-select-sm"
        :debounce="debounce"
        @keydown="keyEvent"
        data-test="main-search-input"
      ></b-input>
    </b-input-group>
    <div v-show="isOpen" class="options-container">
      <div :class="['options', resultsLoaded ? 'borders-results' : '']">
        <ul v-for="(optionKey, index) in Object.keys(options)" :key="index" class="list-unstyled">
          <li v-html="options[optionKey].label"></li>
          <li>
            <ul class="list-unstyled">
              <li v-if="options[optionKey].options.length === 0" v-t="'common.noResults'"></li>
              <li
                v-else
                v-for="(option, index) in options[optionKey].options"
                :key="index"
                @click="select(option)"
                :class="option.disabled ? 'disabled' : ''"
                v-html="formatter(option.text)"
              ></li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { escapeRegExp, HTMLsanitize } from '../../utils/sanitizer'

let handleOutsideClick = null
export default {
  name: 'CustomAutocompGroupedSelect',
  props: {
    value: String,
    options: {},
    placeholder: String,
    debounce: { type: Number, default: 0 },
    // Opt-in, defaults to false. When true, picking a result that has its own `onClick` action
    // (e.g. "go to this product") does not also commit the picked text as an applied search value —
    // see select() below.
    skipInputWhenActionable: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      textValue: '',
      resultsLoaded: false,
    }
  },
  methods: {
    onFocusOut: function () {
      this.$emit('enterpressed', this.textValue)
      this.$emit('input', this.textValue)
      this.isOpen = false
    },
    clearInput: function () {
      this.textValue = ''
      this.isOpen = false
    },
    select(clicked) {
      this.isOpen = false
      this.textValue = clicked.value.name
      if (!(this.skipInputWhenActionable && clicked.onClick)) {
        this.$emit('input', clicked.value.name)
      }
      if (clicked.onClick) {
        clicked.onClick(clicked.value)
      }
    },
    formatter(value) {
      let sanitizedValue = HTMLsanitize(value)
      let reg = new RegExp('(' + escapeRegExp(this.textValue) + ')', 'gi')
      return sanitizedValue.replace(reg, '<b>$1</b>')
    },
    keyEvent(event) {
      if (event.key === 'Enter') {
        this.$emit('enterpressed', this.textValue)
        this.$emit('input', this.textValue)
        this.isOpen = false
      } else {
        this.isOpen = true
      }
    },
  },
  watch: {
    textValue: function (newValue) {
      this.$emit('searchchange', newValue)
    },
    value: function (newValue) {
      this.textValue = newValue
    },
    options: function () {
      this.resultsLoaded = Object.keys(this.options).length > 0
    },
  },
  directives: {
    closable: {
      bind(el, binding, vnode) {
        handleOutsideClick = (e) => {
          e.stopPropagation()
          const { handler, exclude } = binding.value
          let clickedOnExcludedEl = false
          exclude.forEach((refName) => {
            if (!clickedOnExcludedEl) {
              const excludedEl = vnode.context.$refs[refName]
              clickedOnExcludedEl = excludedEl.contains(e.target)
            }
          })
          if (!el.contains(e.target) && !clickedOnExcludedEl) {
            vnode.context[handler]()
          }
        }
        document.addEventListener('click', handleOutsideClick)
        document.addEventListener('touchstart', handleOutsideClick)
      },

      unbind() {
        document.removeEventListener('click', handleOutsideClick)
        document.removeEventListener('touchstart', handleOutsideClick)
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.searchBox * {
  background-color: var(--secondaryBGColor) !important;
  margin: 0 -1px 0 -1px;
}

.searchBox input {
  border-left: 0;
}

.searchBox .input-group-text {
  border-right: 0;
}

.borders-results {
  border: 1px solid #ced4da;
}

.custom-multi-select {
  .custom-select-active {
    color: var(--primary);
    font-weight: 700;
  }

  .options-container {
    position: absolute;
    width: 94.7%;
    z-index: 10;
    background-color: var(--secondaryBGColor);
    padding: 0;
    font-size: 0.9rem;

    .options {
      height: auto;
      max-height: 300px;
      overflow-y: auto;

      ul {
        margin-bottom: 0;

        li {
          cursor: default;
          padding: 0.25rem 0.75rem;

          li:not(.disabled) {
            &:hover {
              background-color: var(--blueVariant);
              color: #000000;
            }
          }
        }
      }
    }
  }
}
</style>
